import { capitalize } from 'lodash';

import {
  ICartEntry,
} from '@rbi-ctg/menu';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { EnablePremiumComboSlotsVariations } from 'state/launchdarkly/variations';
import { useUIContext } from 'state/ui';
import { CartEntryType } from 'utils/cart/types';

/**
 * This hook takes a cart entry item and returns description
 * composed of comboSlot & itemOption selections.
 *
 * Return type can be either in the form of selection names,
 * or a single string containing comma separated selection names
 * depending on options specified.
 *
 * Note: includeModifiers also controls if we show comboSlot items.
 */
export function useComposeDescription(
  item: ICartEntry,
  options: {
    includeQuantity?: boolean;
  } = {}
): string[] {
  const enablePremiumComboSlotsVariation = useFlag<EnablePremiumComboSlotsVariations>(
    LaunchDarklyFlag.ENABLE_PREMIUM_COMBO_SLOTS
  );
  const includeAllPrices =
    enablePremiumComboSlotsVariation !== EnablePremiumComboSlotsVariations.NONE;

  const { includeQuantity = false } = options;
  const { formatCurrencyForLocale } = useUIContext();

  const getDescription = (
    cartEntryChildren: ICartEntry[] = [],
    quantity: number,
  ): string[] => {
    return cartEntryChildren.reduce<string[]>((desc, child) => {
      if (child.type === CartEntryType.item || child.type === CartEntryType.itemOptionModifier) {
        const itemName = (child.name || '').split(' ').map(capitalize).join(' ');
        const displayName =
          includeQuantity && child.quantity > 1 ? `${child.quantity || ''} ${itemName}` : itemName;
        const price = (child.price || 0) * quantity;
        desc.push(
          (child.type === CartEntryType.itemOptionModifier || includeAllPrices) && price > 0
            ? displayName.concat(` + ${formatCurrencyForLocale(price)}`)
            : displayName
        );
      }

      return desc.concat(getDescription(child.children, quantity));
    }, []);
  };

  const description = getDescription(item.children, item.quantity);

  return description;
}
